/**
 * PartnerVehicleRecords — reusable vehicle table with dropdown stage filtering.
 *
 * Ported from fleet-partner. Accepts partnerId as a prop (admin context).
 * Uses server-side pagination (15 records/page) and renders SBT vehicle photos
 * with HoverCard zoom.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { ListPagination } from '@/components/ListPagination';
import { Search, FileText } from 'lucide-react';
import { VehiclePipelineStages } from '@/components/vehicles/VehiclePipeline';
import {
    formatCurrency,
    PROCUREMENT_STAGE_CONFIG,
    CONTRACT_STAGE_CONFIG,
    STAGE_BADGE_STYLES,
    STAGE_LABELS,
    type VehicleStage,
} from '@/lib/format';
import { formatShortDate } from '@/components/vehicles/ProcurementContainers';

interface PartnerVehicleRecordsProps {
    partnerId: number;
    group?: 'procurement' | 'contracts';
}

interface VehiclePipelineRow {
    vehicle_id: number;
    make: string;
    model: string;
    color: string;
    reg_number: string | null;
    procurement_stage: string | null;
    contract_stage: string | null;
    contract_id: number | null;
    sold_at: number;
    total_repaid: number;
    management_rate: number;
    est_arrival_at: string | null;
    ship_date: string | null;
    days_in_current_stage: number;
    listing_url: string | null;
    vehicle_updated_at: string | null;
    vin: string | null;
    manufactured_at: string | null;
    total_count: number;
}

interface StageCount {
    stage: string;
    stage_group: string;
    count: number;
}

function getColorValue(color: string): string {
    const trimmed = color?.trim();
    if (!trimmed) return '#94a3b8';
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    return trimmed;
}

function getSbtPhotoUrl(listingUrl: string | null, updatedAt?: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

const PAGE_SIZE = 15;

export default function PartnerVehicleRecords({ partnerId, group }: PartnerVehicleRecordsProps) {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [stageFilter, setStageFilter] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => { setCurrentPage(1); }, [stageFilter]);

    // Fetch stage counts
    const { data: stageCounts } = useQuery({
        queryKey: ['partner-vehicle-stage-counts', partnerId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_vehicle_stage_counts', {
                p_partner_id: partnerId,
            });
            if (error) throw error;
            return data as StageCount[];
        },
        enabled: !!partnerId,
    });

    // Fetch paginated vehicle data
    const { data: response, isLoading } = useQuery({
        queryKey: ['partner-vehicle-pipeline', partnerId, currentPage, debouncedSearch, stageFilter, group],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_vehicle_pipeline', {
                p_partner_id: partnerId,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                ...(debouncedSearch.trim() ? { p_search: debouncedSearch.trim() } : {}),
                ...(stageFilter ? { p_stage: stageFilter } : {}),
                ...(group ? { p_group: group } : {}),
            });
            if (error) throw error;
            const rows = (data as VehiclePipelineRow[]) || [];
            return {
                rows,
                totalItems: rows.length > 0 ? rows[0].total_count : 0,
            };
        },
        enabled: !!partnerId,
    });

    const vehicles = response?.rows ?? [];
    const totalItems = response?.totalItems ?? 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    // Compute pipeline stages from stage counts
    const { procurementStages, contractStages, procurementCount, contractCount } = useMemo(() => {
        const counts = stageCounts || [];
        const procCounts: Record<string, number> = {};
        const contCounts: Record<string, number> = {};

        PROCUREMENT_STAGE_CONFIG.forEach(s => { procCounts[s.key] = 0; });
        CONTRACT_STAGE_CONFIG.forEach(s => { contCounts[s.key] = 0; });

        counts.forEach(c => {
            if (c.stage_group === 'procurement' && procCounts[c.stage] !== undefined) {
                procCounts[c.stage] = c.count;
            }
            if (c.stage_group === 'contracts' && contCounts[c.stage] !== undefined) {
                contCounts[c.stage] = c.count;
            }
        });

        const procStages = PROCUREMENT_STAGE_CONFIG.map(s => ({
            ...s, count: procCounts[s.key] || 0,
        }));
        const contStages = CONTRACT_STAGE_CONFIG.map(s => ({
            ...s, count: contCounts[s.key] || 0,
        }));

        const pCount = Object.values(procCounts).reduce((s, c) => s + c, 0);
        const cCount = Object.values(contCounts).reduce((s, c) => s + c, 0);

        return {
            procurementStages: procStages,
            contractStages: contStages,
            procurementCount: pCount,
            contractCount: cCount,
        };
    }, [stageCounts]);

    const getStage = (v: VehiclePipelineRow): VehicleStage | null => {
        return (v.contract_stage || v.procurement_stage) as VehicleStage | null;
    };

    const getStatusBadge = (v: VehiclePipelineRow) => {
        const stage = getStage(v);
        if (!stage) return <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">—</Badge>;
        return <Badge variant="outline" className={STAGE_BADGE_STYLES[stage]}>{STAGE_LABELS[stage]}</Badge>;
    };

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                {/* Search + Pipeline Stages */}
                <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search vehicles..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                            <ListPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                    </div>

                    {/* Pipeline stages */}
                    {(!group || group === 'procurement') && (
                        <div className="space-y-1">
                            {!group && (
                                <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                                    Procurement
                                </span>
                            )}
                            <VehiclePipelineStages
                                stages={procurementStages}
                                activeStage={stageFilter}
                                onStageClick={setStageFilter}
                                groupCount={procurementCount}
                            />
                        </div>
                    )}
                    {(!group || group === 'contracts') && (
                        <div className="space-y-1">
                            {!group && (
                                <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                                    Contracts
                                </span>
                            )}
                            <VehiclePipelineStages
                                stages={contractStages}
                                activeStage={stageFilter}
                                onStageClick={setStageFilter}
                                groupCount={contractCount}
                            />
                        </div>
                    )}
                </div>

                {/* Table */}
                {isLoading ? (
                    <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {group === 'contracts' && <TableHead>Contract</TableHead>}
                                    <TableHead>Vehicle</TableHead>
                                    {group === 'procurement' && <TableHead>Identification</TableHead>}
                                    <TableHead>Status</TableHead>
                                    {group === 'procurement' && <TableHead>Est. Arrival</TableHead>}
                                    {group === 'contracts' && <TableHead className="text-right">Repaid</TableHead>}
                                    {group === 'contracts' && <TableHead className="text-right">Mgt Rate</TableHead>}
                                    {!group && <TableHead className="text-right">Contract Value</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {vehicles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                                            {debouncedSearch ? `No vehicles matching "${debouncedSearch}"` : 'No vehicles found'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    vehicles.map((v) => {
                                        const photoUrl = getSbtPhotoUrl(v.listing_url, v.vehicle_updated_at);
                                        return (
                                        <TableRow key={v.vehicle_id}>
                                            {group === 'contracts' && (
                                                <TableCell>
                                                    {v.contract_id ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary shrink-0">
                                                                <FileText className="h-3.5 w-3.5 text-secondary-foreground" />
                                                            </div>
                                                            <span className="font-medium text-sm">#{v.contract_id}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">—</span>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {photoUrl ? (
                                                        <HoverCard openDelay={200} closeDelay={100}>
                                                            <HoverCardTrigger asChild>
                                                                <img
                                                                    src={photoUrl}
                                                                    alt=""
                                                                    className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                    }}
                                                                />
                                                            </HoverCardTrigger>
                                                            <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                                                                <img src={photoUrl} alt={`${v.make} ${v.model}`} className="w-[480px] rounded-md" />
                                                            </HoverCardContent>
                                                        </HoverCard>
                                                    ) : null}
                                                    <div
                                                        className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                                                        style={{ backgroundColor: getColorValue(v.color) }}
                                                    />
                                                    <div>
                                                        <div className="font-medium text-sm">
                                                            {v.make} {v.model}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {group === 'procurement' ? (v.manufactured_at || '') : (v.reg_number?.trim() || '')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            {group === 'procurement' && (
                                                <TableCell>
                                                    <div>
                                                        {v.reg_number?.trim() && (
                                                            <p className="font-medium text-sm">{v.reg_number.trim()}</p>
                                                        )}
                                                        {v.vin && (
                                                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{v.vin}</code>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell>{getStatusBadge(v)}</TableCell>
                                            {group === 'procurement' && (
                                                <TableCell>
                                                    {v.est_arrival_at ? (
                                                        <span className="text-sm">{formatShortDate(v.est_arrival_at)}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">—</span>
                                                    )}
                                                </TableCell>
                                            )}
                                            {group === 'contracts' && (
                                                <TableCell className="text-right">
                                                    <div className="text-sm font-medium tabular-nums">{formatCurrency(v.total_repaid)}</div>
                                                    {v.sold_at > 0 && (
                                                        <div className="text-xs text-muted-foreground tabular-nums">
                                                            of {formatCurrency(v.sold_at)}
                                                        </div>
                                                    )}
                                                </TableCell>
                                            )}
                                            {group === 'contracts' && (
                                                <TableCell className="text-right">
                                                    <span className="text-sm tabular-nums">{parseFloat(v.management_rate.toFixed(2))}%</span>
                                                </TableCell>
                                            )}
                                            {!group && (
                                                <TableCell className="text-right">
                                                    {v.sold_at > 0 ? (
                                                        <span className="text-sm font-medium tabular-nums">{formatCurrency(v.sold_at)}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">—</span>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
